// react
import { useContext, useMemo, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';

// hooks
import { useVerificationGroups } from '../../../../hooks/useVerificationGroups';

// types
import { VerificationGroup } from '../../../../@types';

// mui
import {
	alpha,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import { Add as IconAdd, Group as IconGroup } from '@mui/icons-material';

// design system
import { radius } from '../../../../utils/designSystem';

// v2 components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// local components
import { VerificationGroupsDataTable } from './VerificationGroupsDataTable';

// data
import { COLORS } from '../../../../data';

// Component

export const VerificationGroups = () => {
	const theme = useTheme();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);

	// URL-driven state
	const searchQuery = searchParams.get('search') || '';
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const filterOrderBy = searchParams.get('orderBy') || 'name';
	const filterOrder = (searchParams.get('order') || 'ASC') as 'ASC' | 'DESC';

	// Hook — drives data fetch from URL params
	const {
		create: createGroup,
		data: groups,
		deactivate: deactivateGroup,
		error,
		loading,
		pagination,
		update: updateGroup,
	} = useVerificationGroups({
		projectId: project?.id,
		query: {
			...(searchQuery && { search: searchQuery }),
			limit: parseInt(filterLimit),
			order: filterOrder,
			orderBy: filterOrderBy,
			page: parseInt(filterPage),
		},
	});

	// Dialog state
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingGroup, setEditingGroup] = useState<VerificationGroup | null>(null);
	const [groupName, setGroupName] = useState('');
	const [groupDescription, setGroupDescription] = useState('');

	// Summary stats
	const summaryStats = useMemo(() => {
		const totalMembers = groups.reduce((sum, g) => sum + (g.members?.length || 0), 0);
		const totalLeads = groups.reduce(
			(sum, g) => sum + (g.members?.filter((m) => m.role === 'LEAD').length || 0),
			0
		);
		return { totalGroups: pagination?.total || groups.length, totalLeads, totalMembers };
	}, [groups, pagination]);

	// URL param handlers (matching Contracts.tsx pattern)
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) params.set('search', value);
			else params.delete('search');
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (column: string, direction: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', column);
			params.set('order', direction);
			return params;
		});
	};

	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	// CRUD handlers
	const handleOpenCreate = () => {
		setEditingGroup(null);
		setGroupName('');
		setGroupDescription('');
		setDialogOpen(true);
	};

	const handleEditGroup = (group: VerificationGroup) => {
		setEditingGroup(group);
		setGroupName(group.name);
		setGroupDescription(group.description || '');
		setDialogOpen(true);
	};

	const handleSave = async () => {
		if (!project?.id || !groupName.trim()) return;

		if (editingGroup) {
			const res = await updateGroup(editingGroup.id, {
				description: groupDescription || null,
				name: groupName.trim(),
			});
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			} else {
				setBanner({ message: 'Group updated.', severity: 'success' });
			}
		} else {
			const res = await createGroup({
				description: groupDescription || null,
				name: groupName.trim(),
				projectId: project.id,
			});
			if (res.error) {
				setBanner({ message: res.error.message, severity: 'error' });
			}
		}

		setDialogOpen(false);
		setEditingGroup(null);
	};

	const handleDeactivateGroup = async (group: VerificationGroup) => {
		const res = await deactivateGroup(group.id);
		if (res.error) {
			setBanner({ message: res.error.message, severity: 'error' });
		} else {
			setBanner({ message: `Group "${group.name}" deactivated.`, severity: 'success' });
		}
	};

	// ==========================================================================
	// Render
	// ==========================================================================
	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconGroup}
				title={t('spendPlan.verificationGroups')}
						subtitle={t('spendPlan.verificationGroups.subtitle')}
				variant="condition"
				stats={[
							{ label: t('spendPlan.verificationGroups.groups'), value: summaryStats.totalGroups, color: COLORS.info.main },
							{ label: t('spendPlan.verificationGroups.totalMembers'), value: summaryStats.totalMembers },
							{ label: t('spendPlan.verificationGroups.leads'), value: summaryStats.totalLeads, color: COLORS.warning.main },
				]}
				actions={
					<Button
						onClick={handleOpenCreate}
						startIcon={<IconAdd />}
						sx={{
							borderRadius: radius.md,
							fontWeight: 600,
							px: 3,
							py: 1.25,
							textTransform: 'none',
						}}
						variant="contained">
						{t('spendPlan.verificationGroups.newGroup')}
					</Button>
				}
			/>

			{/* Content */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Error */}
				{error && (
					<Paper
						sx={{
							p: 1.5,
							mb: 2,
							bgcolor: alpha(theme.palette.error.main, 0.06),
							border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
							borderRadius: radius.md,
						}}>
						<Typography variant="body2" color="error">
							{error}
						</Typography>
					</Paper>
				)}

				{/* DataTable */}
				<VerificationGroupsDataTable
					data={groups}
					isLoading={loading}
					searchValue={searchQuery}
					onSearchChange={handleSearchChange}
					orderBy={filterOrderBy}
					order={filterOrder}
					onSortChange={handleSortChange}
					page={parseInt(filterPage)}
					rowsPerPage={parseInt(filterLimit)}
					totalCount={pagination?.total || 0}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleRowsPerPageChange}
					onEditGroup={handleEditGroup}
					onDeactivateGroup={handleDeactivateGroup}
				/>
			</Box>

			{/* Create/Edit Group Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: radius.lg } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>
					{editingGroup ? t('spendPlan.verificationGroups.editGroup') : t('spendPlan.verificationGroups.createGroup')}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField
							label={t('spendPlan.verificationGroups.groupName')}
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							required
							fullWidth
							size="small"
							placeholder={t('spendPlan.verificationGroups.groupNamePlaceholder')}
							autoFocus
						/>
						<TextField
						label={t('spendPlan.verificationGroups.descriptionOptional')}
							value={groupDescription}
							onChange={(e) => setGroupDescription(e.target.value)}
							fullWidth
							size="small"
							multiline
							rows={2}
							placeholder={t('spendPlan.verificationGroups.descriptionPlaceholder')}
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setDialogOpen(false)}
						sx={{ borderRadius: radius.md, textTransform: 'none' }}>
						{t('cancel')}
					</Button>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={!groupName.trim()}
						sx={{ borderRadius: radius.md, textTransform: 'none', fontWeight: 600 }}>
						{editingGroup ? t('spendPlan.verificationGroups.saveChanges') : t('spendPlan.verificationGroups.createGroupButton')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
